/* eslint-disable @typescript-eslint/no-explicit-any */
import { DownloadHttpMethod } from '../typings/enums';
import { Maybe } from '../typings/types';
import { DownloadRequestEchoToken, DownloadRequestToken } from '../utils/commonConstants';
import { ensureTrailingSlash, removeStartingSlash } from '../utils/urlHelper';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios';

const DEFAULT_REQUEST_TIMEOUT_SEC = 120;
const DEFAULT_DOWNLOAD_FILENAME = 'download.txt';

export interface IDownloadOnProgressArgs {
	lengthComputable?: Maybe<number>;
	loaded?: Maybe<number>;
	total?: Maybe<number>;
}

export interface IDownloadOptions {
	useConfirmationToken?: boolean;
	token?: string;
	requestHeaders?: Record<string, string>[];
	requestData?: any;
	fileName?: string;
	successCallback?: () => void;
	errorCallback?: (err: Error) => void;
	onProgressCallback?: (eventArgs: IDownloadOnProgressArgs) => void;
}

export class FileDownloader {
	private axiosInstance: AxiosInstance;
	private requestTimeout: number;
	private basePath: string;
	private requestToken: Maybe<string> = null;

	constructor(baseURL = '', timeout: number = DEFAULT_REQUEST_TIMEOUT_SEC) {
		this.requestTimeout = timeout;
		this.basePath = `${ensureTrailingSlash(window.basePath)}${removeStartingSlash(baseURL)}`;
		this.axiosInstance = axios.create({
			baseURL: this.basePath,
			responseType: 'blob'
		});
	}

	protected createBaseRequest(): AxiosRequestConfig {
		return {
			baseURL: `${ensureTrailingSlash(window.location.origin)}${ensureTrailingSlash(
				removeStartingSlash(this.basePath)
			)}`,
			timeout: 1000 * (this.requestTimeout ?? DEFAULT_REQUEST_TIMEOUT_SEC)
		};
	}

	async downloadFile(
		url: string,
		method: DownloadHttpMethod = DownloadHttpMethod.GET,
		downloadOptions: IDownloadOptions = {},
		cancellationToken: CancelToken = null
	): Promise<void> {
		const onDownloadProgressHandler = (event: IDownloadOnProgressArgs) => {
			if (!event?.lengthComputable || typeof downloadOptions?.onProgressCallback !== 'function') {
				return;
			}

			downloadOptions?.onProgressCallback(event as IDownloadOnProgressArgs);
		};

		const config: AxiosRequestConfig = {
			...this.createBaseRequest(),
			url,
			headers: this.prepareRequestHeaders(downloadOptions),
			onDownloadProgress: onDownloadProgressHandler,
			cancelToken: cancellationToken
		};

		const downloadMethod = (method as string).toLowerCase();
		const request =
			downloadMethod === 'post'
				? this.axiosInstance.post(url, this.getRequestData(downloadOptions), config)
				: this.axiosInstance.get(url, { ...config, params: this.getRequestData(downloadOptions) });

		request
			.then(response => {
				const validationError = this.validateDownloadToken(response, downloadOptions);
				if (validationError) {
					downloadOptions.errorCallback?.(validationError);
					return;
				}

				const saveAsFileName =
					downloadOptions?.fileName ??
					this.tryGetFileName(response.headers['content-disposition']) ??
					DEFAULT_DOWNLOAD_FILENAME;

				this.saveDataAsFile(response.data, saveAsFileName);

				downloadOptions.successCallback?.();
			})
			.catch(async (error: AxiosError) => {
				let errorMessage = localize('An unexpected error has occurred. Please try again.');
				if (error?.response) {
					const responseError = await this.tryGetErrorDetails(error.response);
					if (responseError?.message) {
						errorMessage = responseError?.message;
					} else if (responseError?.Message) {
						errorMessage = responseError?.Message;
					}
				}

				downloadOptions.errorCallback?.(new Error(errorMessage));
			});
	}

	private async tryGetErrorDetails(response: AxiosResponse<Blob>): Promise<Record<string, string>> {
		if (!response) {
			return null;
		}

		try {
			const responseText: string = await response?.data?.text();
			if (responseText) {
				return JSON.parse(responseText);
			}
		} catch {
			// don't throw error to return common message
		}

		return null;
	}

	private validateDownloadToken(response: AxiosResponse<Blob>, options: IDownloadOptions): Maybe<Error> {
		const errorMessage = localize('An unexpected error has occurred. Please try again.');
		if (!response) {
			return new Error(errorMessage);
		}

		if (!options?.useConfirmationToken) {
			return null;
		}

		const echoToken = response.headers[DownloadRequestEchoToken];
		if (echoToken === this.requestToken) {
			return null;
		}

		return new Error(errorMessage);
	}

	private saveDataAsFile(blobData, fileName) {
		const blobDataUrl = window.URL.createObjectURL(blobData);
		const downloadLink = document.createElement('a');
		downloadLink.href = blobDataUrl;
		downloadLink.download = fileName;
		document.body.appendChild(downloadLink);
		downloadLink.click();
		downloadLink.remove();
		window.URL.revokeObjectURL(blobDataUrl);
	}

	private getToken(): string {
		return crypto.randomUUID();
	}

	private tryGetFileName(disposition: Maybe<string>) {
		if (!disposition) {
			return null;
		}

		let fileName = disposition.split(/;(.+)/)[1].split(/=(.+)/)[1];
		if (fileName.toLowerCase().startsWith("utf-8''")) {
			fileName = decodeURIComponent(fileName.replace("utf-8''", ''));
		} else {
			fileName = fileName.replace(/['"]/g, '');
		}

		return fileName;
	}

	private getRequestData(options: IDownloadOptions) {
		if (!options?.requestData) {
			return {};
		}

		return options.requestData;
	}

	private prepareRequestHeaders(options: IDownloadOptions): Record<string, string> {
		if (!options?.useConfirmationToken) {
			this.requestToken = null;
			return {};
		}

		this.requestToken = options.token ?? this.getToken();
		return {
			[DownloadRequestToken]: this.requestToken
		};
	}
}
